import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Phone, Mail, MapPin } from "lucide-react";
import { useLang } from "../LanguageContext";
import { useSettings } from "../SettingsContext";
import { IMG, resolveSiteImage } from "../brand";
import NewsletterForm from "./NewsletterForm";
import HoursTable from "./HoursTable";
import EditableText from "./EditableText";
import EditableImage from "./EditableImage";
import EditableI18n from "./EditableI18n";
import { translations } from "../i18n";

const T_EN = translations.en;
const T_FR = translations.fr;

export default function Footer() {
  const { t, lang } = useLang();
  const { settings } = useSettings();
  const logoUrl = resolveSiteImage(settings, "logo");

  return (
    <footer data-testid="site-footer" className="relative border-t border-border bg-muted/40 mt-32">
      <div className="px-6 md:px-12 lg:px-20 py-20 grid grid-cols-1 lg:grid-cols-12 gap-12">
        {/* Brand */}
        <div className="lg:col-span-4 flex flex-col gap-5">
          <div className="relative h-16 w-16">
            <img src={logoUrl} alt="Queensway Studio" className="h-16 w-16 object-contain rounded-full bg-foreground/95" />
            <EditableImage imageKey="logo" label="Logo" pos="top-4 left-20" />
          </div>
          <h3 className="font-serif text-3xl tracking-tight text-foreground">Queensway Studio</h3>
          <p className="text-sm text-muted-foreground max-w-sm leading-relaxed">
            <EditableText kEn="footer_tagline_en" kFr="footer_tagline_fr" label="Footer tagline"
              fallback={{ en: T_EN.footer.tagline, fr: T_FR.footer.tagline }} />
          </p>
          <a href={settings.instagram_url} target="_blank" rel="noreferrer"
             data-testid="footer-instagram"
             className="qw-link-underline inline-flex items-center gap-2 text-xs tracking-[0.28em] uppercase text-primary mt-2 w-fit">
            <Instagram size={14} strokeWidth={1.2} /> {settings.instagram_handle}
          </a>
        </div>

        {/* Newsletter */}
        <div className="lg:col-span-4">
          <NewsletterForm />
        </div>

        {/* Hours */}
        <div className="lg:col-span-4">
          <p className="qw-overline mb-3"><EditableI18n k="hours.title" label="Hours title" /></p>
          <HoursTable compact />
        </div>
      </div>

      <div className="qw-divider mx-6 md:mx-12 lg:mx-20" />

      <div className="px-6 md:px-12 lg:px-20 py-12 grid grid-cols-1 md:grid-cols-12 gap-8">
        <div className="md:col-span-3">
          <p className="qw-overline mb-3"><EditableI18n k="footer.sitemap" label="Sitemap heading" /></p>
          <div className="flex flex-col gap-2 text-sm">
            <Link to="/services" className="text-foreground/85 hover:text-primary qw-link-underline w-fit"><EditableI18n k="nav.services" label="Nav: services" /></Link>
            <Link to="/gallery" className="text-foreground/85 hover:text-primary qw-link-underline w-fit"><EditableText kEn="txt_nav_gallery_en" kFr="txt_nav_gallery_fr" label="Nav: Gallery" fallback={{ en: "Gallery", fr: "Galerie" }} /></Link>
            <Link to="/journal" className="text-foreground/85 hover:text-primary qw-link-underline w-fit"><EditableI18n k="nav.journal" label="Nav: journal" /></Link>
            <Link to="/about" className="text-foreground/85 hover:text-primary qw-link-underline w-fit"><EditableI18n k="nav.about" label="Nav: about" /></Link>
            <Link to="/booking" className="text-foreground/85 hover:text-primary qw-link-underline w-fit"><EditableI18n k="nav.booking" label="Nav: booking" /></Link>
            <Link to="/contact" className="text-foreground/85 hover:text-primary qw-link-underline w-fit"><EditableI18n k="nav.contact" label="Nav: contact" /></Link>
          </div>
        </div>
        <div className="md:col-span-3">
          <p className="qw-overline mb-3"><EditableText kEn="txt_footer_more_en" kFr="txt_footer_more_fr" label="More heading" fallback={{ en: "More", fr: "Plus" }} /></p>
          <div className="flex flex-col gap-2 text-sm">
            <Link to="/quiz" className="text-foreground/85 hover:text-primary qw-link-underline w-fit"><EditableI18n k="quiz.title" label="Quiz link" /></Link>
            <Link to="/gift-cards" className="text-foreground/85 hover:text-primary qw-link-underline w-fit"><EditableI18n k="giftCards.title" label="Gift cards link" /></Link>
            <Link to="/login" className="text-foreground/85 hover:text-primary qw-link-underline w-fit"><EditableI18n k="auth.login" label="Login link" /></Link>
          </div>
        </div>
        <div className="md:col-span-6">
          <p className="qw-overline mb-3"><EditableI18n k="nav.contact" label="Contact heading" /></p>
          <a href={`tel:${settings.phone_e164}`} className="flex items-center gap-3 text-sm text-foreground/85 hover:text-primary transition-colors mb-2">
            <Phone size={14} strokeWidth={1.2} className="text-primary" /> {settings.phone}
          </a>
          <a href={`mailto:${settings.email}`} className="flex items-center gap-3 text-sm text-foreground/85 hover:text-primary transition-colors mb-2">
            <Mail size={14} strokeWidth={1.2} className="text-primary" /> {settings.email}
          </a>
          <div className="flex items-center gap-3 text-sm text-foreground/85 mb-2">
            <MapPin size={14} strokeWidth={1.2} className="text-primary" /> {settings.location_dakar}
          </div>
          <div className="flex items-center gap-3 text-sm text-foreground/85">
            <MapPin size={14} strokeWidth={1.2} className="text-primary" /> {settings.location_london}
          </div>
        </div>
      </div>

      <div className="px-6 md:px-12 lg:px-20 py-6 border-t border-border flex flex-col md:flex-row justify-between gap-4 text-[0.7rem] tracking-[0.25em] uppercase text-muted-foreground">
        <span>© {new Date().getFullYear()} Queensway Studio. <EditableI18n k="footer.rights" label="Rights text" /></span>
        <span>queenswaystudio.com</span>
      </div>
    </footer>
  );
}
